import React, { PureComponent } from 'react';
import Grid from '@mui/material/Grid/Grid';
import * as dates from "@simplybenefits/sb-dates";
import * as Services from '../../services/index.js';
import * as Theme from '../../theme/index.js';
import NumberInput from '../../components/TextFields/NumberInput.js';
import Text from '../../components/Text.js';
import Button from '../../components/Button.js';
import TextInput from '../../components/TextFields/TextInput.js';
import Modal from '../../components/Modals/Modal.js';
import DateInput from '../../components/TextFields/DateInput.js';
const nameMap = {
  criticalIllness: 'Critical Illness',
  criticalIllnessSpouse: 'Critical Illness (Spouse)',
  criticalIllnessChild: 'Critical Illness (Child))',
  deathDismemberment: 'Accidental Death and Dismemberment',
  dependentLife: 'Dependent Life',
  drugs: 'Drugs',
  employeeAssistance: 'Employee Assistance Program',
  medicalSecondOpinion: 'Medical Second Opinion',
  eldercare: 'Eldercare',
  mentalHealthOnDemand: 'Mental Health On Demand',
  lifeInsurance: 'Life Insurance',
  ltd: 'Long Term Disability',
  std: 'Short Term Disability',
  outOfProvince: 'Out of Province',
  stopLoss: 'Stop Loss',
  majorMedical: 'Major Medical',
  paramedicals: 'Paramedicals',
  eyeExams: 'Eye Exams',
  eyeGlassesAndContacts: 'Eye Glasses & Contacts',
  requiredMedicalEyewear: 'Medically Required Eyewear',
  basicDental: 'Basic Dental',
  majorDental: 'Major Dental',
  orthodontics: 'Orthodontics',
  virtualHealthcare: 'Virtual Healthcare',
  financialAwareness: 'Financial Awareness'
};
const commissionMap = {
  criticalIllness: 'ci',
  criticalIllnessSpouse: 'ci',
  criticalIllnessChild: 'ci',
  deathDismemberment: 'add',
  dependentLife: 'depLife',
  drugs: 'health',
  employeeAssistance: 'efap',
  medicalSecondOpinion: 'efap',
  eldercare: 'efap',
  mentalHealthOnDemand: 'efap',
  virtualHealthcare: 'virtualHealthcare',
  financialAwareness: 'financialAwareness',
  lifeInsurance: 'life',
  ltd: 'ltd',
  std: 'std',
  outOfProvince: 'outOfCountry',
  stopLoss: 'stopLoss',
  majorMedical: 'health',
  paramedicals: 'health',
  eyeExams: 'health',
  eyeGlassesAndContacts: 'health',
  requiredMedicalEyewear: 'health',
  basicDental: 'dental',
  majorDental: 'dental',
  orthodontics: 'dental'
};
const coverageKeyMap = {
  criticalIllness: 'criticalIllness',
  criticalIllnessSpouse: 'criticalIllness',
  criticalIllnessChild: 'criticalIllness',
  deathDismemberment: 'deathDismemberment',
  dependentLife: 'dependentLife',
  drugs: 'drugs',
  employeeAssistance: 'efap',
  medicalSecondOpinion: 'efap',
  eldercare: 'efap',
  mentalHealthOnDemand: 'efap',
  virtualHealthcare: 'virtualHealthcare',
  financialAwareness: 'financialAwareness',
  lifeInsurance: 'lifeInsurance',
  ltd: 'ltd',
  std: 'std',
  outOfProvince: 'outOfCountry',
  stopLoss: 'stopLoss',
  majorMedical: 'healthcare',
  paramedicals: 'healthcare',
  eyeExams: 'healthcare',
  eyeGlassesAndContacts: 'healthcare',
  requiredMedicalEyewear: 'healthcare',
  basicDental: 'dental',
  majorDental: 'dental',
  orthodontics: 'dental'
};
const selectedKey = {
  criticalIllness: 'isSelected',
  criticalIllnessSpouse: 'includeSpouseCI',
  criticalIllnessChild: 'includeChildCI',
  deathDismemberment: 'isSelected',
  dependentLife: 'isSelected',
  drugs: 'isSelected',
  employeeAssistance: 'includeEmployeeAssistance',
  medicalSecondOpinion: 'includeMedicalSecondOpinion',
  eldercare: 'includeEldercare',
  mentalHealthOnDemand: 'includeMentalHealthOnDemand',
  virtualHealthcare: 'isSelected',
  financialAwareness: 'isSelected',
  lifeInsurance: 'isSelected',
  ltd: 'isSelected',
  std: 'isSelected',
  outOfProvince: 'isSelected',
  stopLoss: 'isSelected',
  majorMedical: 'isSelected',
  paramedicals: 'isSelected',
  eyeExams: 'includeEyeExams',
  eyeGlassesAndContacts: 'includeEyeGlassesAndContacts',
  requiredMedicalEyewear: 'includeRequiredSafetyEyeWear',
  basicDental: 'includeBasic',
  majorDental: 'includeMajor',
  orthodontics: 'includeOrthodontics'
};
class PricingDesignForm extends PureComponent {
  state = {
    effectiveDate: dates.getUTC(new Date(), 'YYYY/MM/DD'),
    showEffectiveDateDialog: false,
    errors: {}
  };
  componentDidMount() {
    if (this.props.revision) {
      return null;
    }

    // set all the fields dynamically using the commission rates so they do not have to blur each field to get the new gross rates after a commission change
    this._setDefaults();
    return true;
  }
  _validateEffectiveDate = async () => {
    let effectiveDateError = await Services.validateInput('effectiveDate', this.state.effectiveDate);
    const startOfMonth = dates.getStartOfMonth();
    const effectiveDateUTC = dates.getUTC(this.state.effectiveDate);
    if (effectiveDateUTC < startOfMonth) {
      effectiveDateError = 'Pricing can only be backed dated to the start of the month.';
    }
    await this.setState({
      effectiveDateError
    });
    if (effectiveDateError) {
      return false;
    }
    return true;
  };

  /**
   * When a field is blurred, calculate the new rates and store in global state
   * @param markup
   * @param benefit
   * @param coverageType
   * @param key
   * @param value
   * @private
   */
  _onBlur = (markup, benefit, coverageType, key, value) => {
    // don't allow changes when in revision view
    if (this.props.revision) {
      return;
    }
    let data = {
      [key]: value
    };
    if (coverageType) {
      const netRateWithMarkup = Number(value / (1 - markup / 100)).toFixed(3);
      const grossRate = Number(netRateWithMarkup / (1 - this.props.commissions[commissionMap[benefit]] / 100)).toFixed(3);
      const commissionRate = Number(grossRate - netRateWithMarkup).toFixed(3);
      data = {
        netRate: value,
        netRateWithMarkup: Number(netRateWithMarkup),
        commissionRate: Number(commissionRate),
        grossRate: Number(grossRate)
      };
    } else {
      coverageType = key; // set the coverage type to the key so we set the state correctly
    }
    this.props.savePricingInput(this.props.classID, this.props.designID, benefit, coverageType, data);
  };
  _setPastRate = (benefit, coverageType, value) => {
    // don't allow changes when in revision view
    if (this.props.revision) {
      return;
    }
    this.props.savePricingInput(this.props.classID, this.props.designID, benefit, coverageType, {
      pastRates: {
        grossRate: value
      }
    });
  };
  _handleDropDownChange = (benefit, key, value) => {
    // don't allow changes when in revision view
    if (this.props.revision) {
      return;
    }
    this.props.savePricingInput(this.props.classID, this.props.designID, benefit, 'carrier', {
      [key]: value
    });
  };
  _validateForm = async () => {
    let isValid = true;
    const planDesign = this.props.designs[this.props.classID][this.props.designID];
    const effectiveDateValid = await this._validateEffectiveDate();
    if (!effectiveDateValid) {
      return null;
    }
    const promises = Object.keys(nameMap).map(async key => {
      const coverage = planDesign[coverageKeyMap[key]];
      if (coverage && (!coverage.isSelected || !coverage[selectedKey[key]])) {
        return null;
      }
      const markupError = await Services.validateInput('checkEmpty', `${isNaN(this.props.pricing[key].markup) ? '' : this.props.pricing[key].markup}`);
      const soloNetRateError = await Services.validateInput('checkEmpty', `${isNaN(this.props.pricing[key].solo.netRate) ? '' : this.props.pricing[key].solo.netRate}`);
      const duoNetRateError = await Services.validateInput('checkEmpty', `${isNaN(this.props.pricing[key].duo.netRate) ? '' : this.props.pricing[key].duo.netRate}`);
      const familyNetRateError = await Services.validateInput('checkEmpty', `${isNaN(this.props.pricing[key].family.netRate) ? '' : this.props.pricing[key].family.netRate}`);
      const carrierError = await Services.validateInput('checkEmpty', `${this.props.pricing[key].carrier || ''}`);
      if (markupError || soloNetRateError || duoNetRateError || familyNetRateError || carrierError) {
        isValid = false;
      }
      this.setState({
        errors: {
          ...this.state.errors,
          [`${key}markupError`]: markupError,
          [`${key}soloNetRateError`]: soloNetRateError,
          [`${key}duoNetRateError`]: duoNetRateError,
          [`${key}familyNetRateError`]: familyNetRateError,
          [`${key}carrierError`]: carrierError
        }
      });
      return null;
    });
    await Promise.all(promises);
    return isValid;
  };
  _setDefaultPrices = async () => {
    const planDesign = this.props.designs[this.props.classID][this.props.designID];
    const pricing = {
      ...this.props.pricing
    };
    const promises = Object.keys(nameMap).map(key => {
      const coverage = planDesign[coverageKeyMap[key]];
      if (coverage && (!coverage.isSelected || !coverage[selectedKey[key]])) {
        pricing[key] = {
          carrier: null,
          markup: 10,
          solo: {
            netRate: 0,
            netRateWithMarkup: 0,
            commissionRate: 0,
            grossRate: 0,
            pastRates: {
              grossRate: 0
            }
          },
          duo: {
            netRate: 0,
            netRateWithMarkup: 0,
            commissionRate: 0,
            grossRate: 0,
            pastRates: {
              grossRate: 0
            }
          },
          family: {
            netRate: 0,
            netRateWithMarkup: 0,
            commissionRate: 0,
            grossRate: 0,
            pastRates: {
              grossRate: 0
            }
          }
        };
      }
      return null;
    });
    await Promise.all(promises);
    return pricing;
  };
  _savePricing = async () => {
    // don't allow changes when in revision view
    if (this.props.revision) {
      return null;
    }
    window.scroll(0, 0);
    const isFormValid = await this._validateForm();
    if (!isFormValid) {
      // eslint-disable-next-line consistent-return
      return null;
    }

    // make sure any fields that were not set get defaulted to 0
    const data = await this._setDefaultPrices();
    const success = await this.props.savePricing(this.props.classID, this.props.designID, data, dates.getUTC(this.state.effectiveDate));
    if (success) {
      this.setState({
        effectiveDate: dates.getUTC(new Date(), 'YYYY/MM/DD'),
        showEffectiveDateDialog: false
      });
    }

    // eslint-disable-next-line consistent-return
    return null;
  };
  _toggleEffectiveDateDialog = () => {
    this.setState({
      showEffectiveDateDialog: !this.state.showEffectiveDateDialog
    });
  };
  _setDefaults = () => {
    const planDesign = this.props.designs[this.props.classID][this.props.designID];
    const pricing = this.props.pricing;
    Object.keys(nameMap).map(key => {
      const coverage = planDesign[coverageKeyMap[key]];
      if (!pricing[key]) {
        return null;
      }
      if (coverage && (!coverage.isSelected || !coverage[selectedKey[key]] || !pricing[key])) {
        return null;
      }

      // eslint-disable-next-line no-undef
      if (process.env.NODE_ENV === 'development') {
        this._onBlur(pricing[key].markup, key, 'solo', 'netRate', pricing[key] && pricing[key].solo.netRate !== 0 ? pricing[key].solo.netRate : 1);
        this._onBlur(pricing[key].markup, key, 'duo', 'netRate', pricing[key] && pricing[key].solo.netRate !== 0 ? pricing[key].solo.netRate : 2);
        this._onBlur(pricing[key].markup, key, 'family', 'netRate', pricing[key] && pricing[key].solo.netRate !== 0 ? pricing[key].solo.netRate : 3);
        this._handleDropDownChange(key, 'carrier', pricing[key].carrier || 'Allstate');
      } else {
        this._onBlur(pricing[key].markup, key, 'solo', 'netRate', !pricing[key] || pricing[key].solo.netRate === null ? '' : pricing[key].solo.netRate);
        this._onBlur(pricing[key].markup, key, 'duo', 'netRate', !pricing[key] || pricing[key].duo.netRate === null ? '' : pricing[key].duo.netRate);
        this._onBlur(pricing[key].markup, key, 'family', 'netRate', !pricing[key] || pricing[key].family.netRate === null ? '' : pricing[key].family.netRate);
      }
      return true;
    });
  };
  render() {
    const {
      pricing
    } = this.props;
    const planDesign = this.props.designs[this.props.classID][this.props.designID];
    return /*#__PURE__*/React.createElement(React.Fragment, null, Object.keys(nameMap).map(key => {
      const coverage = planDesign[coverageKeyMap[key]];
      if (coverage && (!coverage.isSelected || !coverage[selectedKey[key]])) {
        return null;
      }
      return /*#__PURE__*/React.createElement(React.Fragment, {
        key: key
      }, /*#__PURE__*/React.createElement(Text, {
        color: Theme.colors.darkBlue,
        medium: true,
        bold: true,
        display: "block",
        align: "left",
        margin: "20px 0 0 0"
      }, nameMap[key]), /*#__PURE__*/React.createElement("div", {
        style: {
          marginBottom: 50
        }
      }, /*#__PURE__*/React.createElement(Grid, {
        container: true,
        direction: "row",
        alignItems: "center",
        spacing: 2
      }, /*#__PURE__*/React.createElement(Grid, {
        item: true,
        xs: 12,
        sm: 4
      }, /*#__PURE__*/React.createElement(TextInput, {
        label: "Carrier",
        fullWidth: true,
        required: true,
        select: true,
        variant: "outlined",
        selectOptions: ['Sunlife', 'Simply Benefits', 'La Capitale', 'Equitable Life', 'Empire', 'Orion', 'SSQ', 'Kii', 'Advica', 'AIG', 'Allstate', 'ASO', 'Maple', 'Gotodoctor', 'Dialogue', 'Camden', 'Fenchurch', 'Wawanesa', 'Desjardins', 'Industrial Alliance', 'Industrial Alliance Special Risk', 'Enriched Academy'].sort(),
        error: this.state.errors[`${key}carrierError`],
        value: !pricing[key] || !pricing[key].carrier ? '' : pricing[key].carrier,
        onChange: e => this._handleDropDownChange(key, 'carrier', e.target.value)
      })), /*#__PURE__*/React.createElement(Grid, {
        item: true,
        xs: 12,
        sm: 4
      }, /*#__PURE__*/React.createElement(NumberInput, {
        label: "Markup",
        fullWidth: true,
        variant: "outlined",
        suffix: "%",
        decimalPlaces: 3,
        value: !pricing[key] || pricing[key].markup === null ? '' : pricing[key].markup,
        onBlur: e => this._onBlur(pricing[key].markup, key, null, 'markup', e.target.value),
        error: this.state.errors[`${key}markupError`]
      })), /*#__PURE__*/React.createElement(Grid, {
        item: true,
        xs: 12,
        sm: 4
      }, /*#__PURE__*/React.createElement(NumberInput, {
        label: "Commission Percentage",
        fullWidth: true,
        value: this.props.commissions[commissionMap[key]],
        variant: "outlined",
        suffix: "% ",
        decimalPlaces: 3,
        disabled: true
      })), /*#__PURE__*/React.createElement(Grid, {
        item: true,
        xs: 12,
        sm: 4
      }, /*#__PURE__*/React.createElement(NumberInput, {
        label: "Solo (Net Rate)",
        fullWidth: true,
        variant: "outlined",
        prefix: "$ ",
        decimalPlaces: 3,
        value: !pricing[key] || pricing[key]?.solo?.netRate === null ? '' : pricing[key]?.solo?.netRate,
        onBlur: e => this._onBlur(pricing[key]?.markup, key, 'solo', 'netRate', e.target.value),
        error: this.state.errors[`${key}soloNetRateError`]
      })), /*#__PURE__*/React.createElement(Grid, {
        item: true,
        xs: 12,
        sm: 4
      }, /*#__PURE__*/React.createElement(NumberInput, {
        label: "Solo (Commission Rate)",
        fullWidth: true,
        value: !pricing[key] || pricing[key]?.solo?.commissionRate === null ? '' : pricing[key]?.solo?.commissionRate,
        variant: "outlined",
        prefix: "$ ",
        decimalPlaces: 3,
        disabled: true
      })), /*#__PURE__*/React.createElement(Grid, {
        item: true,
        xs: 12,
        sm: 4
      }, /*#__PURE__*/React.createElement(NumberInput, {
        label: "Solo (Gross Rate)",
        fullWidth: true,
        value: !pricing[key] || pricing[key]?.solo?.grossRate === null ? '' : pricing[key]?.solo?.grossRate,
        variant: "outlined",
        prefix: "$ ",
        decimalPlaces: 3,
        disabled: true
      })), /*#__PURE__*/React.createElement(Grid, {
        item: true,
        xs: 12,
        sm: 4
      }, /*#__PURE__*/React.createElement(NumberInput, {
        label: "Duo (Net Rate)",
        fullWidth: true,
        variant: "outlined",
        prefix: "$ ",
        decimalPlaces: 3,
        value: !pricing[key] || pricing[key]?.duo?.netRate === null ? '' : pricing[key]?.duo?.netRate,
        onBlur: e => this._onBlur(pricing[key]?.markup, key, 'duo', 'netRate', e.target.value),
        error: this.state.errors[`${key}duoNetRateError`]
      })), /*#__PURE__*/React.createElement(Grid, {
        item: true,
        xs: 12,
        sm: 4
      }, /*#__PURE__*/React.createElement(NumberInput, {
        label: "Duo (Commission Rate)",
        fullWidth: true,
        value: !pricing[key] || pricing[key]?.duo?.commissionRate === null ? '' : pricing[key]?.duo?.commissionRate,
        variant: "outlined",
        prefix: "$ ",
        decimalPlaces: 3,
        disabled: true
      })), /*#__PURE__*/React.createElement(Grid, {
        item: true,
        xs: 12,
        sm: 4
      }, /*#__PURE__*/React.createElement(NumberInput, {
        label: "Duo (Gross Rate)",
        fullWidth: true,
        value: !pricing[key] || pricing[key]?.duo?.grossRate === null ? '' : pricing[key]?.duo?.grossRate,
        variant: "outlined",
        prefix: "$ ",
        decimalPlaces: 3,
        disabled: true
      })), /*#__PURE__*/React.createElement(Grid, {
        item: true,
        xs: 12,
        sm: 4
      }, /*#__PURE__*/React.createElement(NumberInput, {
        label: "Family (Net Rate)",
        fullWidth: true,
        variant: "outlined",
        prefix: "$ ",
        decimalPlaces: 3,
        value: !pricing[key] || pricing[key]?.family?.netRate === null ? '' : pricing[key]?.family?.netRate,
        onBlur: e => this._onBlur(pricing[key]?.markup, key, 'family', 'netRate', e.target.value),
        error: this.state.errors[`${key}familyNetRateError`]
      })), /*#__PURE__*/React.createElement(Grid, {
        item: true,
        xs: 12,
        sm: 4
      }, /*#__PURE__*/React.createElement(NumberInput, {
        label: "Family (Commission Rate)",
        fullWidth: true,
        value: !pricing[key] || pricing[key]?.family?.commissionRate === null ? '' : pricing[key]?.family?.commissionRate,
        variant: "outlined",
        prefix: "$ ",
        decimalPlaces: 3,
        disabled: true
      })), /*#__PURE__*/React.createElement(Grid, {
        item: true,
        xs: 12,
        sm: 4
      }, /*#__PURE__*/React.createElement(NumberInput, {
        label: "Family (Gross Rate)",
        fullWidth: true,
        value: !pricing[key] || pricing[key]?.family?.grossRate === null ? '' : pricing[key]?.family?.grossRate,
        variant: "outlined",
        prefix: "$ ",
        decimalPlaces: 3,
        disabled: true
      }))), /*#__PURE__*/React.createElement(Text, {
        color: Theme.colors.darkBlue,
        medium: true,
        light: true,
        display: "block",
        align: "left",
        margin: "20px 0 0 0"
      }, "Enter any past rates from the Carrier to be used in the pricing breakdown summary."), /*#__PURE__*/React.createElement(Grid, {
        container: true,
        direction: "row",
        alignItems: "center",
        spacing: 2
      }, /*#__PURE__*/React.createElement(Grid, {
        item: true,
        xs: 12,
        sm: 4
      }, /*#__PURE__*/React.createElement(NumberInput, {
        label: "Solo (Gross Rate)",
        fullWidth: true,
        value: !pricing[key] || !pricing[key]?.solo?.pastRates ? '' : pricing[key]?.solo?.pastRates?.grossRate,
        variant: "outlined",
        prefix: "$ ",
        decimalPlaces: 3,
        disabled: !this.props.showPastPricing,
        onBlur: e => this._setPastRate(key, 'solo', e.target.value)
      })), /*#__PURE__*/React.createElement(Grid, {
        item: true,
        xs: 12,
        sm: 4
      }, /*#__PURE__*/React.createElement(NumberInput, {
        label: "Duo (Gross Rate)",
        fullWidth: true,
        value: !pricing[key] || !pricing[key]?.duo?.pastRates ? '' : pricing[key]?.duo?.pastRates?.grossRate,
        variant: "outlined",
        prefix: "$ ",
        decimalPlaces: 3,
        disabled: !this.props.showPastPricing,
        onBlur: e => this._setPastRate(key, 'duo', e.target.value)
      })), /*#__PURE__*/React.createElement(Grid, {
        item: true,
        xs: 12,
        sm: 4
      }, /*#__PURE__*/React.createElement(NumberInput, {
        label: "Family (Gross Rate)",
        fullWidth: true,
        value: !pricing[key] || !pricing[key]?.family?.pastRates ? '' : pricing[key]?.family?.pastRates?.grossRate,
        variant: "outlined",
        prefix: "$ ",
        decimalPlaces: 3,
        disabled: !this.props.showPastPricing,
        onBlur: e => this._setPastRate(key, 'family', e.target.value)
      })))));
    }), !this.props.revision && /*#__PURE__*/React.createElement(Grid, {
      container: true,
      direction: "row",
      alignItems: "center",
      justifyContent: "flex-end"
    }, /*#__PURE__*/React.createElement(Button, {
      onClick: () => {
        if (!this.props.isQuote && !this.props.isRevision) {
          this._toggleEffectiveDateDialog();
        } else {
          this._savePricing();
        }
      },
      color: "primary",
      marginTop: "40px",
      marginBottom: "40px"
    }, "Save Pricing For Design")), /*#__PURE__*/React.createElement(Modal, {
      open: this.state.showEffectiveDateDialog,
      onClose: this._toggleEffectiveDateDialog,
      title: "Set Effective Date",
      primaryButton: {
        label: "Submit",
        onClick: this._savePricing
      },
      loader: this.props.loader
    }, /*#__PURE__*/React.createElement(Text, {
      medium: true
    }, "Please provide the date you would like the change(s) to be effective:"), /*#__PURE__*/React.createElement(DateInput, {
      label: "Effective Date",
      value: this.state.effectiveDate,
      onChange: e => {
        this.setState({
          effectiveDate: e.target.value
        });
      },
      error: Boolean(this.state.effectiveDateError),
      helperText: this.state.effectiveDateError,
      fullWidth: true,
      required: true
    }), /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "10px 0 0 0"
    }, "By clicking Submit below, the changes made will be applied and effective on the date provided above. Back dated changes will have adjustments applied using current rates.")));
  }
}
export default PricingDesignForm;